import { type DesignElementKey } from "@hooks/use-design-element-manager";

type PlatformStyleInfo = {
  color: string;
};
type PlatformStyles = Partial<Record<DesignElementKey, PlatformStyleInfo>>;

export enum NB_COLOR {
  COSMIC = "#7081FF",
  PLUTO = "#71D5D5",
  VENUS = "#C825A8",
  MARS = "#F05F33",
  SUN = "#FFD247",
  JUPITER = "#F3971A",
  SATURN = "#681E06",
  ALIEN = "#5CBF64",
  NEBULA = "#BCFBBA",
  INTERSTELLAR = "#021330",
  GRAY = "#C1C5CD",
  GRAYM2 = "#E0E1E6",
  GRAYM3 = "#F1F1F2",
}

export const ALL_COLORS = [
  NB_COLOR.COSMIC,
  NB_COLOR.PLUTO,
  NB_COLOR.VENUS,
  NB_COLOR.MARS,
  NB_COLOR.SUN,
  NB_COLOR.JUPITER,
  NB_COLOR.SATURN,
  NB_COLOR.ALIEN,
  NB_COLOR.NEBULA,
  NB_COLOR.INTERSTELLAR,
  NB_COLOR.GRAY,
];

function getRepeatablePrng() {
  return (size: number) => 0;
}

function getRandomPrng() {
  return (size: number) => Math.floor(Math.random()) * size;
}

export function getRandomNbColor(excluding: string[]) {
  const excludingSet = new Set(excluding);
  const filteredColors = ALL_COLORS.filter((color) => !excludingSet.has(color));
  const colors = filteredColors.length ? filteredColors : ALL_COLORS;
  const prng = getRepeatablePrng();
  return colors[prng(colors.length)];
}

export const PLATFORM_COLOR_MAP: PlatformStyles = {
  facebook: {
    color: NB_COLOR.COSMIC,
  },
  google: {
    color: NB_COLOR.PLUTO,
  },
  tikTok: {
    color: NB_COLOR.VENUS,
  },
  youTube: {
    color: NB_COLOR.MARS,
  },
  snapchat: {
    color: NB_COLOR.SUN,
  },
  instagram: {
    color: NB_COLOR.JUPITER,
  },
  email: {
    color: NB_COLOR.SATURN,
  },
  pinterest: {
    color: "#C9353D",
  },
  microsoft: {
    color: NB_COLOR.ALIEN,
  },
  twitter: {
    color: NB_COLOR.NEBULA,
  },
};

export const DEFAULT_COLOR = NB_COLOR.INTERSTELLAR;

export const ORDERED_BAR_CHART_COLORS = [
  NB_COLOR.COSMIC,
  NB_COLOR.PLUTO,
  NB_COLOR.VENUS,
  NB_COLOR.MARS,
  NB_COLOR.SUN,
  NB_COLOR.JUPITER,
  NB_COLOR.SATURN,
  "#C9353D",
  NB_COLOR.ALIEN,
  NB_COLOR.NEBULA,
];

export const ORDERED_BAR_CHART_SHADES_MAP = {
  [NB_COLOR.COSMIC]: [
    "#4454FF",
    "#5D6EFF",
    "#7081FF",
    "#A9B3FF",
    "#CDD3FF",
    "#EAECFF",
    "#F9FAFF",
  ],
  [NB_COLOR.PLUTO]: ["#32B2B2", "#51C3C3", "#71D5D5", "#B8EAEA", "#F1FBFB"],
  [NB_COLOR.VENUS]: ["#9D056F", "#B2158B", "#C825A8", "#E392D3", "#FAE9F6"],
  [NB_COLOR.MARS]: ["#E2230A", "#E9411F", "#F05F33", "#F7AF99", "#FEEFEB"],
  [NB_COLOR.SUN]: ["#FFB823", "#FFC738", "#FFD247", "#FFE8A3", "#FFFBED"],
  [NB_COLOR.JUPITER]: ["#EB6C0A", "#F08413", "#F3971A", "#F9CB8C", "#FEF5E8"],
  [NB_COLOR.SATURN]: ["#3D0B02", "#551604", "#681E06", "#B38E82", "#F0E9E6"],
  "#C9353D": ["#E2230A", "#E9411F", "#F05F33", "#F7AF99", "#FEEFEB"],
  [NB_COLOR.ALIEN]: ["#218F27", "#3FA746", "#5CBF64", "#ADDFB1", "#EFF9F0"],
  [NB_COLOR.NEBULA]: ["#8BF788", "#A3F9A1", "#BCFBBA", "#DDFDDC", "#F2FEF1"],
};

export type SupportedMonochromeColor =
  keyof typeof ORDERED_BAR_CHART_SHADES_MAP;
